<template>
  <div class="user-page py-8 px-4">
    <div class="max-w-7xl mx-auto">
      <!-- 页面标题 -->
      <div class="text-center mb-12">
        <h1 class="text-5xl md:text-6xl font-orbitron font-bold mb-4">
          <span class="text-gradient">User</span> Center
        </h1>
        <p class="text-cyber-light/70 text-lg max-w-2xl mx-auto">
          Manage your profile, view your game collection, and customize your experience
        </p>
      </div>

      <!-- 用户信息卡片 -->
      <div class="cyber-card mb-8">
        <div class="flex items-center space-x-6">
          <div class="w-24 h-24 bg-gradient-to-r from-cyber-blue to-cyber-purple rounded-full flex items-center justify-center">
            <span class="text-cyber-dark font-orbitron font-bold text-2xl">{{ user.avatar }}</span>
          </div>
          <div class="flex-1">
            <h2 class="text-2xl font-orbitron font-bold text-cyber-light mb-2">{{ user.username }}</h2>
            <p class="text-cyber-light/70 mb-4">{{ user.email }}</p>
            <div class="flex items-center space-x-6">
              <div class="text-center">
                <div class="text-xl font-orbitron font-bold text-cyber-blue">{{ user.stats.gamesPlayed }}</div>
                <div class="text-cyber-light/70 text-sm">Games Played</div>
              </div>
              <div class="text-center">
                <div class="text-xl font-orbitron font-bold text-cyber-purple">{{ user.stats.favorites }}</div>
                <div class="text-cyber-light/70 text-sm">Favorites</div>
              </div>
              <div class="text-center">
                <div class="text-xl font-orbitron font-bold text-cyber-green">{{ user.stats.achievements }}</div>
                <div class="text-cyber-light/70 text-sm">Achievements</div>
              </div>
            </div>
          </div>
          <button class="cyber-button">Edit Profile</button>
        </div>
      </div>

      <!-- 主要内容区域 -->
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <!-- 侧边栏导航 -->
        <div class="lg:col-span-1">
          <div class="cyber-card">
            <nav class="space-y-2">
              <button 
                v-for="tab in tabs" 
                :key="tab.id"
                @click="activeTab = tab.id"
                :class="[
                  'w-full text-left px-4 py-3 rounded-lg transition-all duration-300',
                  activeTab === tab.id 
                    ? 'bg-cyber-blue text-cyber-dark' 
                    : 'text-cyber-light hover:bg-cyber-gray/50'
                ]"
              >
                <div class="flex items-center space-x-3">
                  <component :is="tab.icon" class="w-5 h-5" />
                  <span>{{ tab.name }}</span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        <!-- 主要内容 -->
        <div class="lg:col-span-3">
          <!-- 收藏的游戏 -->
          <div v-if="activeTab === 'favorites'" class="cyber-card">
            <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">My Favorites</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="game in userFavorites" :key="game.id" 
                   class="cyber-card-hover cursor-pointer"
                   @click="navigateToGame(game.id)">
                <div class="w-full h-32 bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-lg mb-3"></div>
                <h4 class="font-orbitron font-bold mb-2 text-cyber-light">{{ game.title }}</h4>
                <p class="text-cyber-light/70 text-sm line-clamp-2 mb-3">{{ game.description }}</p>
                <div class="flex items-center justify-between">
                  <span class="cyber-badge text-xs">{{ game.category }}</span>
                  <span class="text-cyber-green font-bold">${{ game.price }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 游戏历史 -->
          <div v-if="activeTab === 'history'" class="cyber-card">
            <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">Play History</h3>
            <div class="space-y-4">
              <div v-for="game in playHistory" :key="game.id" 
                   class="flex items-center space-x-4 p-4 bg-cyber-gray/30 rounded-lg">
                <div class="w-16 h-16 bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-lg flex-shrink-0"></div>
                <div class="flex-1">
                  <h4 class="font-orbitron font-bold text-cyber-light">{{ game.title }}</h4>
                  <p class="text-cyber-light/70 text-sm">Last played: {{ game.lastPlayed }}</p>
                  <p class="text-cyber-light/70 text-sm">Play time: {{ game.playTime }}</p>
                </div>
                <button class="cyber-button text-sm">Play Again</button>
              </div>
            </div>
          </div>

          <!-- 成就 -->
          <div v-if="activeTab === 'achievements'" class="cyber-card">
            <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">Achievements</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="achievement in achievements" :key="achievement.id" 
                   class="cyber-card text-center"
                   :class="{ 'opacity-50': !achievement.unlocked }">
                <div class="w-16 h-16 bg-gradient-to-r from-cyber-yellow to-cyber-green rounded-full mx-auto mb-4 flex items-center justify-center">
                  <svg class="w-8 h-8 text-cyber-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                  </svg>
                </div>
                <h4 class="font-orbitron font-bold mb-2 text-cyber-light">{{ achievement.name }}</h4>
                <p class="text-cyber-light/70 text-sm mb-2">{{ achievement.description }}</p>
                <div class="text-xs text-cyber-light/50">
                  {{ achievement.unlocked ? 'Unlocked' : 'Locked' }}
                </div>
              </div>
            </div>
          </div>

          <!-- 设置 -->
          <div v-if="activeTab === 'settings'" class="cyber-card">
            <h3 class="text-2xl font-orbitron font-bold mb-6 text-cyber-light">Settings</h3>
            <div class="space-y-6">
              <!-- 个人资料设置 -->
              <div>
                <h4 class="text-lg font-semibold mb-4 text-cyber-light">Profile Settings</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-cyber-light text-sm font-medium mb-2">Username</label>
                    <input type="text" v-model="user.username" class="cyber-input w-full" />
                  </div>
                  <div>
                    <label class="block text-cyber-light text-sm font-medium mb-2">Email</label>
                    <input type="email" v-model="user.email" class="cyber-input w-full" />
                  </div>
                </div>
              </div>

              <!-- 偏好设置 -->
              <div>
                <h4 class="text-lg font-semibold mb-4 text-cyber-light">Preferences</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-cyber-light text-sm font-medium mb-2">Language</label>
                    <select v-model="user.preferences.language" class="cyber-select w-full">
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-cyber-light text-sm font-medium mb-2">Currency</label>
                    <select v-model="user.preferences.currency" class="cyber-select w-full">
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- 通知设置 -->
              <div>
                <h4 class="text-lg font-semibold mb-4 text-cyber-light">Notifications</h4>
                <div class="space-y-3">
                  <label class="flex items-center space-x-3">
                    <input type="checkbox" v-model="user.preferences.emailNotifications" class="cyber-input" />
                    <span class="text-cyber-light">Email notifications</span>
                  </label>
                  <label class="flex items-center space-x-3">
                    <input type="checkbox" v-model="user.preferences.gameUpdates" class="cyber-input" />
                    <span class="text-cyber-light">Game updates and news</span>
                  </label>
                  <label class="flex items-center space-x-3">
                    <input type="checkbox" v-model="user.preferences.communityUpdates" class="cyber-input" />
                    <span class="text-cyber-light">Community updates</span>
                  </label>
                </div>
              </div>

              <button class="cyber-button">Save Settings</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const activeTab = ref('favorites')

// 用户数据
const user = ref({
  username: 'CyberGamer2024',
  email: 'gamer@example.com',
  avatar: 'CG',
  stats: {
    gamesPlayed: 47,
    favorites: 12,
    achievements: 23
  },
  preferences: {
    language: 'en',
    currency: 'USD',
    emailNotifications: true,
    gameUpdates: true,
    communityUpdates: false
  }
})

// 标签页配置
const tabs = ref([
  { id: 'favorites', name: 'Favorites', icon: 'HeartIcon' },
  { id: 'history', name: 'Play History', icon: 'ClockIcon' },
  { id: 'achievements', name: 'Achievements', icon: 'StarIcon' },
  { id: 'settings', name: 'Settings', icon: 'CogIcon' }
])

// 收藏的游戏
const userFavorites = ref([
  {
    id: '1',
    title: 'Cyberpunk 2077',
    description: 'An open-world action-adventure story set in Night City.',
    category: 'RPG',
    price: 59.99
  },
  {
    id: '2',
    title: 'Ghostrunner',
    description: 'A hardcore FPP slasher packed with lightning-fast action.',
    category: 'Action',
    price: 39.99
  },
  {
    id: '3',
    title: 'The Ascent',
    description: 'A solo and co-op action-shooter RPG.',
    category: 'RPG',
    price: 34.99
  }
])

// 游戏历史
const playHistory = ref([
  {
    id: '1',
    title: 'Cyberpunk 2077',
    lastPlayed: '2 hours ago',
    playTime: '45 hours'
  },
  {
    id: '2',
    title: 'Ghostrunner',
    lastPlayed: '1 day ago',
    playTime: '12 hours'
  },
  {
    id: '3',
    title: 'The Ascent',
    lastPlayed: '3 days ago',
    playTime: '8 hours'
  }
])

// 成就
const achievements = ref([
  {
    id: 1,
    name: 'First Steps',
    description: 'Complete your first game',
    unlocked: true
  },
  {
    id: 2,
    name: 'Collector',
    description: 'Add 10 games to favorites',
    unlocked: true
  },
  {
    id: 3,
    name: 'Marathon Runner',
    description: 'Play for 100 hours total',
    unlocked: false
  },
  {
    id: 4,
    name: 'Social Butterfly',
    description: 'Join 5 community events',
    unlocked: true
  },
  {
    id: 5,
    name: 'Explorer',
    description: 'Try 5 different game genres',
    unlocked: false
  },
  {
    id: 6,
    name: 'Night Owl',
    description: 'Play games for 7 consecutive days',
    unlocked: false
  }
])

const navigateToGame = (gameId) => {
  router.push(`/games/${gameId}`)
}

// 图标组件
const HeartIcon = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
  </svg>`
}

const ClockIcon = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
  </svg>`
}

const StarIcon = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
  </svg>`
}

const CogIcon = {
  template: `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
  </svg>`
}
</script> 